import { z } from 'zod';

/**
 * Kontrakt modułu AI (ARCHITECTURE §10). Wspólne typy dla bramki AI (`@fos/core/ai`) i adapterów
 * dostawców (`@fos/integrations/ai`). Bez zależności serwerowych (działa też w przeglądarce).
 *
 * Model i dostawca zawsze z konfiguracji (ai_settings albo env), nigdy zahardkodowane.
 */

/** Profile zadań: szybkie i tanie, dokładne, wykonawcze (agentowe). */
export const AI_PROFILES = ['fast', 'smart', 'exec'] as const;
export type AiProfile = (typeof AI_PROFILES)[number];
export const aiProfileSchema = z.enum(AI_PROFILES);

/** Obsługiwani dostawcy. `fake` = deterministyczny adapter bez sieci (domyślny). */
export const AI_PROVIDER_IDS = ['fake', 'anthropic', 'openai'] as const;
export type AiProviderId = (typeof AI_PROVIDER_IDS)[number];
export const aiProviderIdSchema = z.enum(AI_PROVIDER_IDS);

export const AI_JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'cancelled', 'budget_exceeded'] as const;
export type AiJobStatus = (typeof AI_JOB_STATUSES)[number];

/**
 * Wskazówka zadania. Realni dostawcy jej nie potrzebują (działają z promptu), adapter `fake`
 * wybiera po niej regułę: zadania z listy, czas z tekstu, podsumowanie.
 */
export const AI_TASK_HINTS = [
  'extract_tasks',
  'parse_time',
  'summarize',
  'draft',
  'chat',
  // Copilot (Fala 4): adapter fake odpowiada deterministycznie, realni dostawcy ignorują podpowiedź
  'client_summary',
  'draft_email',
  'tasks_from_text',
  'memory',
  'day_summary',
  'classify',
] as const;
export type AiTaskHint = (typeof AI_TASK_HINTS)[number];

/**
 * Poziom rozumowania modelu (profil w `ai_settings.profiles`, Fala 9 — DECISIONS 2026-10-07 „Copilot na start przez
 * OpenAI”). Brak = domyślny poziom modelu (parametr nie jest wysyłany). Adapter bez możliwości `reasoningEffort`
 * pomija ustawienie.
 */
export const AI_REASONING_EFFORTS = ['none', 'low', 'medium', 'high'] as const;
export type AiReasoningEffort = (typeof AI_REASONING_EFFORTS)[number];
export const aiReasoningEffortSchema = z.enum(AI_REASONING_EFFORTS);

export interface AiCapabilities {
  tools: boolean;
  structuredOutput: boolean;
  vision: boolean;
  streaming: boolean;
  /** adapter wysyła poziom rozumowania z profilu (OpenAI Responses `reasoning.effort`); brak = nie */
  reasoningEffort?: boolean;
}

export type AiContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; toolUseId: string; content: string; isError?: boolean };

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string | AiContentBlock[];
  /**
   * Surowa odpowiedź dostawcy (np. bloki `thinking` Anthropic), odsyłana bez zmian w pętli narzędzi
   * do tego samego dostawcy. Nie zapisujemy jej w bazie (bez prywatnego toku rozumowania).
   */
  providerRaw?: { provider: string; content: unknown };
}

export interface AiToolDefinition {
  name: string;
  description: string;
  /** JSON Schema wejścia narzędzia */
  inputSchema: Record<string, unknown>;
}

export interface AiToolCall {
  id: string;
  name: string;
  input: unknown;
}

export interface AiGenerateRequest {
  /** model z konfiguracji (ai_settings.profiles albo AI_MODEL_* w env) */
  model: string;
  system?: string;
  messages: AiMessage[];
  tools?: AiToolDefinition[];
  maxTokens?: number;
  task?: AiTaskHint;
  /**
   * JSON Schema oczekiwanej odpowiedzi (structured output, Fala 4). Adapter z `structuredOutput` wymusza format
   * i zwraca wynik w `structured`; bramka odrzuca wywołanie dostawcy bez tej możliwości (jawny błąd).
   */
  responseSchema?: Record<string, unknown>;
  /** poziom rozumowania profilu (adapter z możliwością `reasoningEffort`; inne go pomijają) */
  reasoningEffort?: AiReasoningEffort;
  signal?: AbortSignal;
}

export type AiStopReason = 'end' | 'tool_use' | 'max_tokens' | 'refusal' | 'other';

/**
 * Zużycie tokenów. `inputTokens` = całe wejście (RAZEM z częścią z cache), `cachedInputTokens` = jego część odczytana
 * z cache (tańsza, `cachedInputPer1M`); `outputTokens` = całe wyjście RAZEM z tokenami rozumowania (dostawcy liczą
 * je jako wyjście), `reasoningTokens` = ta część (informacyjnie).
 */
export interface AiUsage {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens?: number;
  reasoningTokens?: number;
}

export interface AiGenerateResponse {
  provider: string;
  model: string;
  text: string;
  toolCalls: AiToolCall[];
  stopReason: AiStopReason;
  usage: AiUsage;
  /** true = adapter fake: nic nie zostało wysłane do dostawcy */
  simulated: boolean;
  /** wynik strukturalny (adapter fake: zadania, czas, podsumowanie) */
  structured?: unknown;
  /** do odesłania w kolejnym kroku pętli narzędzi (patrz AiMessage.providerRaw) */
  raw?: AiMessage['providerRaw'];
}

export interface AiProvider {
  readonly id: string;
  readonly simulated: boolean;
  readonly capabilities: AiCapabilities;
  generate(req: AiGenerateRequest): Promise<AiGenerateResponse>;
}

/**
 * Cena modelu w PLN za 1 mln tokenów (konfiguracja workspace'u, `ai_settings.price_table`). `cachedInputPer1M` =
 * cena wejścia odczytanego z cache (Fala 9; brak = jak `inputPer1M`). Tokeny rozumowania liczą się jako wyjście.
 */
export const aiModelPriceSchema = z.object({
  inputPer1M: z.string(),
  cachedInputPer1M: z.string().optional(),
  outputPer1M: z.string(),
});
export type AiModelPrice = z.infer<typeof aiModelPriceSchema>;

/** Profil w `ai_settings.profiles`: model i (Fala 9) opcjonalny poziom rozumowania. */
export const aiProfileConfigSchema = z.object({
  model: z.string().min(1),
  reasoningEffort: aiReasoningEffortSchema.optional(),
});
export type AiProfileConfig = z.infer<typeof aiProfileConfigSchema>;

export const aiProfilesConfigSchema = z.object({
  fast: aiProfileConfigSchema.optional(),
  smart: aiProfileConfigSchema.optional(),
  exec: aiProfileConfigSchema.optional(),
});
export type AiProfilesConfig = z.infer<typeof aiProfilesConfigSchema>;

export const AI_DEFAULT_MAX_TOOL_ITERATIONS = 6;
export const AI_DEFAULT_TIMEOUT_MS = 60_000;

/** Kwota budżetu AI w PLN (numeric(12,2), string). */
const aiBudgetSchema = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, { message: 'Nieprawidłowa kwota' });

/**
 * Pola `ai_settings` (Ustawienia → AI i pamięć; Fala 4). Brak wiersza = ustawienia instalacji z env (adapter fake).
 * `perUserBudgetPln` = domyślny miesięczny limit osoby, gdy członkostwo nie ma własnego (`ai_monthly_limit_pln`);
 * `classifyIncoming` = klasyfikacja przychodzących maili przez AI (wyłączona → „Zwykłe” bez AI, mail zostaje w Inboxie).
 */
export const aiSettingsFieldsSchema = z.object({
  enabled: z.boolean(),
  provider: aiProviderIdSchema,
  profiles: aiProfilesConfigSchema,
  monthlyBudgetPln: aiBudgetSchema.nullable(),
  perUserBudgetPln: aiBudgetSchema.nullable(),
  maxToolIterations: z.number().int().min(1).max(20),
  timeoutMs: z.number().int().min(5_000).max(600_000),
  priceTable: z.record(aiModelPriceSchema),
  classifyIncoming: z.boolean(),
});
export type AiSettingsFields = z.infer<typeof aiSettingsFieldsSchema>;

/** Wynik strukturalny adaptera fake dla `extract_tasks`. */
export interface AiExtractedTasks {
  tasks: { title: string; source: 'list' | 'todo' }[];
}

/** Wynik strukturalny adaptera fake dla `parse_time` (np. „REA 1h40 poprawki od 10:20”). */
export interface AiParsedTime {
  clientHint: string | null;
  durationSeconds: number | null;
  start: string | null;
  end: string | null;
  description: string;
}
